import { useQuery } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { ArrowLeft, FileText, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DateCell } from "@/components/DateCell";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface PortalQuoteLine {
  id: string;
  lineNumber: number;
  description: string | null;
  quantity: string;
  unitPrice: string;
  extendedAmount: string;
}

interface PortalQuote {
  id: string;
  quoteNumber: string;
  status: string;
  quoteDate: string;
  expirationDate?: string;
  totalPrice: string;
  account?: { name: string };
  lines?: PortalQuoteLine[];
}

function getStatusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "accepted":
    case "approved":
      return "default";
    case "sent":
    case "pending":
      return "secondary";
    case "rejected":
    case "expired":
      return "destructive";
    default:
      return "outline";
  }
}

function formatCurrency(value: string | number) {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export default function PortalQuoteDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/portal/quotes/:id");
  const [, setLocation] = useLocation();
  const quoteId = params?.id;

  const { data: quote, isLoading } = useQuery<PortalQuote>({
    queryKey: ["/api/portal/quotes", quoteId],
    queryFn: () => api.get<PortalQuote>(`/portal/quotes/${quoteId}`, sessionId),
    enabled: !!quoteId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Sales quote not found</p>
        <Button className="mt-4" onClick={() => setLocation("/portal/quotes")}>
          Back to Sales Quotes
        </Button>
      </div>
    );
  }

  const lines = quote.lines || [];
  const subtotal = lines.reduce((sum, l) => sum + parseFloat(l.extendedAmount || "0"), 0);
  const total = parseFloat(quote.totalPrice || "0");

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/portal/quotes")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Sales Quote {quote.quoteNumber}</h1>
            <p className="text-sm text-muted-foreground">
              Created on <DateCell value={quote.quoteDate} data-testid="text-quote-date-header" />
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              Sales Quote Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lines.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-center">Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <p className="font-medium">{item.description || "—"}</p>
                      </TableCell>
                      <TableCell className="text-center">
                        {Number(item.quantity || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.extendedAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-center py-4">No items in this sales quote</p>
            )}

            <Separator className="my-4" />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold text-base">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sales Quote Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge variant={getStatusVariant(quote.status)}>
                  {quote.status.replace(/_/g, " ")}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sales Quote Date</span>
                <DateCell value={quote.quoteDate} className="text-sm" />
              </div>
              {quote.expirationDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Expires</span>
                  <DateCell value={quote.expirationDate} className="text-sm" />
                </div>
              )}
              {quote.account?.name && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Account</span>
                  <span className="text-sm font-medium">{quote.account.name}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
